// ─────────────────────────────────────────────────────────
//  i18n — Lightweight internationalization (TR/EN/AR)
// ─────────────────────────────────────────────────────────

export type Locale = "tr" | "en" | "ar";

const STORAGE_KEY = "ehille_locale";

export function getLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "tr" || stored === "en" || stored === "ar") return stored;
  return "tr";
}

export function setLocale(locale: Locale) {
  localStorage.setItem(STORAGE_KEY, locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
}

// ── Translation dictionary ───────────────────────────────
type TranslationKey = keyof typeof translations.tr;

const translations = {
  tr: {
    // ── Nav ──
    nav_home: "Ana Sayfa",
    nav_halka: "Halka",
    nav_tally: "Cetele",
    nav_score: "Skor",
    nav_messages: "Mesajlar",
    nav_settings: "Ayarlar",

    // ── Auth ──
    login: "Giris Yap",
    register: "Kayit Ol",
    email: "E-posta",
    password: "Sifre",
    name: "Ad Soyad",
    email_placeholder: "ornek@email.com",
    password_placeholder: "En az 8 karakter",
    name_placeholder: "Adinizi girin",
    or: "veya",
    google_login: "Google ile devam et",
    loading: "Yukleniyor...",
    create_account: "Hesap Olustur",

    // ── Home ──
    good_morning: "Gunaydin",
    good_afternoon: "Iyi gunler",
    good_evening: "Iyi aksamlar",
    daily_progress: "Gunluk Ilerleme",
    goals_completed: "hedef tamamlandi",
    mandatory_left: "zorunlu kaldi",
    weekly_quiz: "Haftalik Sinav",
    quiz_desc: "Bilginizi test edin, puan kazanin",
    new_badge: "Yeni",
    all: "Tumu",
    mandatory: "Zorunlu",
    personal: "Kisisel",
    points: "puan",
    penalty: "Ceza",
    sadaka_debt: "Sadaka Borcu",
    sadaka_debt_desc: "Gecmis hedeflerden borcunuz var",
    already_completed: "Zaten tamamlandi",

    // ── Groups ──
    select_group: "Halkanizi Secin",
    select_group_desc: "Bir gruba katilarak hedeflerinizi birlikte takip edin",
    join_group: "Gruba Katil",
    create_group: "Yeni Halka Olustur",
    create_group_desc: "Kendi grubunuzu kurun",
    group_password: "Grup Sifresi",
    group_name: "Halka Adi",
    group_name_placeholder: "orn: Istanbul Genclik Halkasi",
    password_hint: "Uyeler bu sifreyle katilacak",
    skip_for_now: "Simdilik atla",
    enter_password: "Katilmak icin sifre girin",
    back: "Geri",
    joining: "Katiliniyor...",
    creating: "Olusturuluyor...",

    // ── Tally ──
    tally: "Cetele",
    weekly: "Haftalik",
    monthly: "Aylik",
    total_points: "Toplam Puan",
    avg_success: "Ort. Basari",
    streak: "Seri",
    weekly_points: "Haftalik Puan",
    monthly_points: "Aylik Puan",
    monthly_calendar: "Aylik Takvim",
    day_details: "Gun Detaylari",
    day_streak: "gun seri",

    // ── Scoreboard ──
    scoreboard: "Skor Tablosu",
    all_time: "Tum Zamanlar",
    you_badge: "Sen",
    today: "Bugun",

    // ── Quiz ──
    quiz_title: "Haftalik Sinav",
    question: "Soru",
    next: "Sonraki",
    previous: "Onceki",
    finish_quiz: "Sinavi Bitir",
    submitting: "Gonderiliyor...",
    correct_answers: "dogru cevap",
    close: "Kapat",

    // ── Settings ──
    settings: "Ayarlar",
    customize: "Ozellestir",
    change_password: "Sifre Degistir",
    change_password_desc: "Sifrenizi guncelleyin",
    appearance: "Gorunum",
    appearance_desc: "Tema ve gorunumu degistirin",
    notification_settings: "Bildirim Ayarlari",
    notification_desc: "Hatirlatici ve takvimi ozellestiriniz",
    language: "Dil",
    language_desc: "Dilinizi secin",
    help_support: "Yardim ve Destek",
    terms: "Kullanim Kosullari",
    terms_desc: "Sartlar ve kosullar",
    privacy: "Gizlilik Politikasi",
    privacy_desc: "Verilerinizi koruyoruz",
    help: "Yardim",
    help_desc: "SSS ve destek",
    feedback: "Geri Bildirim",
    feedback_desc: "Goruslerinizi paylasın",
    logout: "Cikis Yap",
    pay_sadaka: "Sadaka Borcunu Ode",
    pay_sadaka_desc: "borcunuz var",
    pay: "Ode",
    day_streak_label: "Gun Seri",
    no_debt: "Yok",

    // ── Sub-pages ──
    current_password: "Mevcut Sifre",
    new_password: "Yeni Sifre",
    confirm_password: "Sifre Tekrar",
    save_changes: "Degisiklikleri Kaydet",
    theme: "Tema",
    dark_mode: "Karanlik Mod",
    light_mode: "Aydinlik Mod",
    system_mode: "Sistem",
    reminder_frequency: "Hatirlatma Sikligi",
    morning_reminder: "Sabah Hatirlatmasi",
    evening_reminder: "Aksam Hatirlatmasi",
    goal_reminders: "Hedef Hatirlatmalari",
    quiz_notifications: "Sinav Bildirimleri",
    sadaka_alerts: "Sadaka Uyarilari",
    enabled: "Acik",
    disabled: "Kapali",
    faq: "Sikca Sorulan Sorular",
    contact_us: "Bize Ulasin",
    send_feedback: "Geri Bildirim Gonder",
    feedback_placeholder: "Goruslerinizi yazin...",
    send: "Gonder",
    sent_success: "Gonderildi, tesekkurler!",

    // ── Terms / Privacy ──
    terms_title: "Kullanim Kosullari",
    privacy_title: "Gizlilik Politikasi",
    last_updated: "Son guncelleme",

    // FAQ
    faq_q1: "Ehille nedir?",
    faq_a1: "Ehille, gunluk hedeflerinizi takip etmenizi, kendinizi denetlemenizi ve sadaka sistemiyle ozkontrolunuzu gelistirmenizi saglayan bir uygulamadir.",
    faq_q2: "Sadaka borcu nasil hesaplanir?",
    faq_a2: "Her gece 00:00'da zorunlu hedefleriniz kontrol edilir. Tamamlanmayanlar icin belirlenen sadaka miktari borcunuza eklenir.",
    faq_q3: "Gruplara nasil katilirim?",
    faq_a3: "Grup sifresini veya QR kodunu kullanarak bir gruba katilabilirsiniz. Grubunuzun zorunlu hedefleri otomatik olarak listenize eklenir.",
    faq_q4: "Puanlama nasil calisir?",
    faq_a4: "Her hedefin belirli bir puani vardir. Hedefleri tamamladikca puan kazanirsiniz. Haftalik sinavlardan da ekstra puan alabilirsiniz.",
    faq_q5: "Verilerim guvenli mi?",
    faq_a5: "Evet. Verileriniz Cloudflare'in kuresel ag altyapisinda sifrelenmis sekilde saklanir.",

    // ── Halka / Groups ──
    my_circle: "Halkam",
    join_with_code: "Kod ile Katil",
    join_with_qr: "QR Kod ile Katil",
    circle_code: "Halka Kodu",
    circle_members: "Uyeler",
    leave_circle: "Halkadan Ayril",
    no_circle: "Henuz bir halkaya katilmadiniz",

    // ── Friends ──
    friends: "Arkadaslar",
    close_friends: "Yakin Arkadaslar",
    add_friend: "Arkadas Ekle",
    friend_requests: "Arkadas Istekleri",
    accept: "Kabul Et",
    reject: "Reddet",
    search_friends: "Arkadas Ara",
    view_goals: "Hedeflerini Gor",

    // ── Goal Marketplace ──
    goal_store: "Hedef Magazasi",
    browse_goals: "Hedefleri Kesfet",
    import_goal: "Hedefi Ekle",
    suggested_goals: "Onerilen Hedefler",
    from_friends: "Arkadaslarindan",
    popular_goals: "Populer Hedefler",
    search_goals: "Hedef Ara...",
  },

  en: {
    nav_home: "Home",
    nav_halka: "Circle",
    nav_tally: "Tally",
    nav_messages: "Messages",
    nav_score: "Score",
    nav_settings: "Settings",
    login: "Sign In",
    register: "Sign Up",
    email: "Email",
    password: "Password",
    name: "Full Name",
    email_placeholder: "example@email.com",
    password_placeholder: "At least 8 characters",
    name_placeholder: "Enter your name",
    or: "or",
    google_login: "Continue with Google",
    loading: "Loading...",
    create_account: "Create Account",
    good_morning: "Good morning",
    good_afternoon: "Good afternoon",
    good_evening: "Good evening",
    daily_progress: "Daily Progress",
    goals_completed: "goals completed",
    mandatory_left: "mandatory left",
    weekly_quiz: "Weekly Quiz",
    quiz_desc: "Test your knowledge, earn points",
    new_badge: "New",
    all: "All",
    mandatory: "Mandatory",
    personal: "Personal",
    points: "pts",
    penalty: "Penalty",
    sadaka_debt: "Sadaqa Debt",
    sadaka_debt_desc: "You have outstanding debt from missed goals",
    already_completed: "Already completed",
    select_group: "Select Your Circle",
    select_group_desc: "Join a group to track your goals together",
    join_group: "Join Group",
    create_group: "Create New Circle",
    create_group_desc: "Set up your own group",
    group_password: "Group Password",
    group_name: "Circle Name",
    group_name_placeholder: "e.g. Youth Growth Circle",
    password_hint: "Members will join with this password",
    skip_for_now: "Skip for now",
    enter_password: "Enter password to join",
    back: "Back",
    joining: "Joining...",
    creating: "Creating...",
    tally: "Tally",
    weekly: "Weekly",
    monthly: "Monthly",
    total_points: "Total Points",
    avg_success: "Avg. Success",
    streak: "Streak",
    weekly_points: "Weekly Points",
    monthly_points: "Monthly Points",
    monthly_calendar: "Monthly Calendar",
    day_details: "Day Details",
    day_streak: "day streak",
    scoreboard: "Scoreboard",
    all_time: "All Time",
    you_badge: "You",
    today: "Today",
    quiz_title: "Weekly Quiz",
    question: "Question",
    next: "Next",
    previous: "Previous",
    finish_quiz: "Finish Quiz",
    submitting: "Submitting...",
    correct_answers: "correct answers",
    close: "Close",
    settings: "Settings",
    customize: "Customize",
    change_password: "Change Password",
    change_password_desc: "Update your password",
    appearance: "Appearance",
    appearance_desc: "Change theme and display",
    notification_settings: "Notification Settings",
    notification_desc: "Customize reminders and calendar",
    language: "Language",
    language_desc: "Select your language",
    help_support: "Help & Support",
    terms: "Terms of Use",
    terms_desc: "Terms and conditions",
    privacy: "Privacy Policy",
    privacy_desc: "We protect your data",
    help: "Help",
    help_desc: "FAQ and support",
    feedback: "Feedback",
    feedback_desc: "Share your thoughts",
    logout: "Sign Out",
    pay_sadaka: "Pay Sadaqa Debt",
    pay_sadaka_desc: "you owe",
    pay: "Pay",
    day_streak_label: "Day Streak",
    no_debt: "None",
    current_password: "Current Password",
    new_password: "New Password",
    confirm_password: "Confirm Password",
    save_changes: "Save Changes",
    theme: "Theme",
    dark_mode: "Dark Mode",
    light_mode: "Light Mode",
    system_mode: "System",
    reminder_frequency: "Reminder Frequency",
    morning_reminder: "Morning Reminder",
    evening_reminder: "Evening Reminder",
    goal_reminders: "Goal Reminders",
    quiz_notifications: "Quiz Notifications",
    sadaka_alerts: "Sadaqa Alerts",
    enabled: "Enabled",
    disabled: "Disabled",
    faq: "Frequently Asked Questions",
    contact_us: "Contact Us",
    send_feedback: "Send Feedback",
    feedback_placeholder: "Write your thoughts...",
    send: "Send",
    sent_success: "Sent, thank you!",
    terms_title: "Terms of Use",
    privacy_title: "Privacy Policy",
    last_updated: "Last updated",
    faq_q1: "What is Ehille?",
    faq_a1: "Ehille is an app that helps you track daily goals, monitor yourself, and develop self-discipline through a sadaqa system.",
    faq_q2: "How is sadaqa debt calculated?",
    faq_a2: "Every night at midnight, your mandatory goals are checked. Incomplete ones add the specified amount to your debt.",
    faq_q3: "How do I join groups?",
    faq_a3: "Use a group password or QR code to join. The group's mandatory goals are automatically added to your list.",
    faq_q4: "How does scoring work?",
    faq_a4: "Each goal has a point value. Complete goals to earn points. You can also earn bonus points from weekly quizzes.",
    faq_q5: "Is my data safe?",
    faq_a5: "Yes. Your data is stored encrypted on Cloudflare's global network infrastructure.",
    my_circle: "My Circle",
    join_with_code: "Join with Code",
    join_with_qr: "Join with QR",
    circle_code: "Circle Code",
    circle_members: "Members",
    leave_circle: "Leave Circle",
    no_circle: "You haven't joined a circle yet",
    friends: "Friends",
    close_friends: "Close Friends",
    add_friend: "Add Friend",
    friend_requests: "Friend Requests",
    accept: "Accept",
    reject: "Reject",
    search_friends: "Search Friends",
    view_goals: "View Goals",
    goal_store: "Goal Store",
    browse_goals: "Browse Goals",
    import_goal: "Import Goal",
    suggested_goals: "Suggested Goals",
    from_friends: "From Friends",
    popular_goals: "Popular Goals",
    search_goals: "Search goals...",
  },

  ar: {
    nav_home: "الرئيسية",
    nav_halka: "الحلقة",
    nav_tally: "السجل",
    nav_messages: "الرسائل",
    nav_score: "النقاط",
    nav_settings: "الإعدادات",
    login: "تسجيل الدخول",
    register: "إنشاء حساب",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    name: "الاسم الكامل",
    email_placeholder: "example@email.com",
    password_placeholder: "٨ أحرف على الأقل",
    name_placeholder: "أدخل اسمك",
    or: "أو",
    google_login: "المتابعة مع جوجل",
    loading: "جاري التحميل...",
    create_account: "إنشاء حساب",
    good_morning: "صباح الخير",
    good_afternoon: "مساء الخير",
    good_evening: "مساء الخير",
    daily_progress: "التقدم اليومي",
    goals_completed: "أهداف مكتملة",
    mandatory_left: "إلزامي متبقي",
    weekly_quiz: "اختبار أسبوعي",
    quiz_desc: "اختبر معلوماتك واكسب نقاطاً",
    new_badge: "جديد",
    all: "الكل",
    mandatory: "إلزامي",
    personal: "شخصي",
    points: "نقاط",
    penalty: "غرامة",
    sadaka_debt: "دين الصدقة",
    sadaka_debt_desc: "لديك دين من الأهداف الفائتة",
    already_completed: "مكتمل بالفعل",
    select_group: "اختر حلقتك",
    select_group_desc: "انضم إلى مجموعة لتتابع أهدافك معاً",
    join_group: "انضم للمجموعة",
    create_group: "إنشاء حلقة جديدة",
    create_group_desc: "أنشئ مجموعتك الخاصة",
    group_password: "كلمة مرور المجموعة",
    group_name: "اسم الحلقة",
    group_name_placeholder: "مثال: حلقة شباب الإيمان",
    password_hint: "سينضم الأعضاء بهذه الكلمة",
    skip_for_now: "تخطي الآن",
    enter_password: "أدخل كلمة المرور للانضمام",
    back: "رجوع",
    joining: "جاري الانضمام...",
    creating: "جاري الإنشاء...",
    tally: "السجل",
    weekly: "أسبوعي",
    monthly: "شهري",
    total_points: "مجموع النقاط",
    avg_success: "متوسط النجاح",
    streak: "سلسلة",
    weekly_points: "نقاط أسبوعية",
    monthly_points: "نقاط شهرية",
    monthly_calendar: "التقويم الشهري",
    day_details: "تفاصيل اليوم",
    day_streak: "يوم متتالي",
    scoreboard: "لوحة النتائج",
    all_time: "كل الأوقات",
    you_badge: "أنت",
    today: "اليوم",
    quiz_title: "اختبار أسبوعي",
    question: "سؤال",
    next: "التالي",
    previous: "السابق",
    finish_quiz: "إنهاء الاختبار",
    submitting: "جاري الإرسال...",
    correct_answers: "إجابات صحيحة",
    close: "إغلاق",
    settings: "الإعدادات",
    customize: "تخصيص",
    change_password: "تغيير كلمة المرور",
    change_password_desc: "تحديث كلمة المرور",
    appearance: "المظهر",
    appearance_desc: "تغيير السمة والعرض",
    notification_settings: "إعدادات الإشعارات",
    notification_desc: "تخصيص التذكيرات والتقويم",
    language: "اللغة",
    language_desc: "اختر لغتك",
    help_support: "المساعدة والدعم",
    terms: "شروط الاستخدام",
    terms_desc: "الشروط والأحكام",
    privacy: "سياسة الخصوصية",
    privacy_desc: "نحمي بياناتك",
    help: "المساعدة",
    help_desc: "الأسئلة الشائعة والدعم",
    feedback: "التعليقات",
    feedback_desc: "شارك أفكارك",
    logout: "تسجيل الخروج",
    pay_sadaka: "دفع دين الصدقة",
    pay_sadaka_desc: "عليك دين",
    pay: "ادفع",
    day_streak_label: "أيام متتالية",
    no_debt: "لا يوجد",
    current_password: "كلمة المرور الحالية",
    new_password: "كلمة المرور الجديدة",
    confirm_password: "تأكيد كلمة المرور",
    save_changes: "حفظ التغييرات",
    theme: "السمة",
    dark_mode: "الوضع الداكن",
    light_mode: "الوضع الفاتح",
    system_mode: "النظام",
    reminder_frequency: "تكرار التذكير",
    morning_reminder: "تذكير الصباح",
    evening_reminder: "تذكير المساء",
    goal_reminders: "تذكيرات الأهداف",
    quiz_notifications: "إشعارات الاختبار",
    sadaka_alerts: "تنبيهات الصدقة",
    enabled: "مفعل",
    disabled: "معطل",
    faq: "الأسئلة الشائعة",
    contact_us: "اتصل بنا",
    send_feedback: "إرسال تعليق",
    feedback_placeholder: "اكتب أفكارك...",
    send: "إرسال",
    sent_success: "تم الإرسال، شكراً!",
    terms_title: "شروط الاستخدام",
    privacy_title: "سياسة الخصوصية",
    last_updated: "آخر تحديث",
    faq_q1: "ما هو إهلّة؟",
    faq_a1: "إهلّة تطبيق يساعدك على تتبع أهدافك اليومية ومراقبة نفسك وتطوير الانضباط الذاتي من خلال نظام الصدقة.",
    faq_q2: "كيف يُحسب دين الصدقة؟",
    faq_a2: "كل ليلة عند منتصف الليل، يتم فحص أهدافك الإلزامية. الأهداف غير المكتملة تضيف المبلغ المحدد إلى دينك.",
    faq_q3: "كيف أنضم إلى المجموعات؟",
    faq_a3: "استخدم كلمة مرور المجموعة أو رمز QR للانضمام. تُضاف الأهداف الإلزامية للمجموعة تلقائياً إلى قائمتك.",
    faq_q4: "كيف يعمل نظام النقاط؟",
    faq_a4: "لكل هدف قيمة نقاط محددة. أكمل الأهداف لكسب النقاط. يمكنك أيضاً كسب نقاط إضافية من الاختبارات الأسبوعية.",
    faq_q5: "هل بياناتي آمنة؟",
    faq_a5: "نعم. يتم تخزين بياناتك مشفرة على البنية التحتية لشبكة Cloudflare العالمية.",
    my_circle: "حلقتي",
    join_with_code: "انضم بالرمز",
    join_with_qr: "انضم بـ QR",
    circle_code: "رمز الحلقة",
    circle_members: "الأعضاء",
    leave_circle: "مغادرة الحلقة",
    no_circle: "لم تنضم إلى حلقة بعد",
    friends: "الأصدقاء",
    close_friends: "الأصدقاء المقربون",
    add_friend: "إضافة صديق",
    friend_requests: "طلبات الصداقة",
    accept: "قبول",
    reject: "رفض",
    search_friends: "البحث عن أصدقاء",
    view_goals: "عرض الأهداف",
    goal_store: "متجر الأهداف",
    browse_goals: "تصفح الأهداف",
    import_goal: "استيراد هدف",
    suggested_goals: "أهداف مقترحة",
    from_friends: "من الأصدقاء",
    popular_goals: "أهداف شائعة",
    search_goals: "البحث عن أهداف...",
  },
} as const;

export function t(key: TranslationKey, locale?: Locale): string {
  const l = locale ?? getLocale();
  return translations[l]?.[key] ?? translations.tr[key] ?? key;
}

export function useT() {
  const locale = getLocale();
  return (key: TranslationKey) => t(key, locale);
}

export const LOCALE_NAMES: Record<Locale, string> = {
  tr: "Turkce",
  en: "English",
  ar: "العربية",
};

export const LOCALE_FLAGS: Record<Locale, string> = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦",
};
